import { SurveyActionBar, SurveyElementBase } from "survey-react-ui";
import { Action, ActionContainer, Base, SurveyModel } from "survey-core";
import { Button } from "./Button";

interface ActionBarProps {
  model: ActionContainer<Action>;
  survey: SurveyModel;
}

interface ActionButtonProps {
  action: Action;
  isPrimary?: boolean;
  isValidatingOnServer: boolean;
  isQuestionsLoading: boolean;
  isSubmitting: boolean;
  titleOverride?: string;
}

class ActionButton extends SurveyElementBase<ActionButtonProps, object> {
  protected getStateElement(): Base {
    return this.props.action;
  }

  renderElement() {
    const {
      action,
      isPrimary,
      isValidatingOnServer,
      isQuestionsLoading,
      isSubmitting,
      titleOverride,
    } = this.props;
    return (
      <Button
        type={isPrimary ? "primary" : "default"}
        disabled={
          !action.enabled || (isPrimary && (isQuestionsLoading || isSubmitting))
        }
        onClick={() => action.action()}
        loading={
          (isValidatingOnServer || isQuestionsLoading || isSubmitting) &&
          !!isPrimary
        }
      >
        {titleOverride || action.title}
      </Button>
    );
  }
}

export class ActionBar extends SurveyActionBar {
  declare props: ActionBarProps;

  get survey(): SurveyModel {
    return this.props.survey;
  }

  protected getStateElements(): Base[] {
    const elements = super.getStateElements();
    if (this.survey) {
      elements.push(this.survey);
    }
    return elements;
  }

  get isValidatingOnServer(): boolean {
    return this.survey?.isValidatingOnServer ?? false;
  }

  get isCurrentPageQuestionsLoading(): boolean {
    return (
      this.survey?.getPropertyValue("isCurrentPageQuestionsLoading") ?? false
    );
  }

  get isSubmitting(): boolean {
    return this.survey?.getPropertyValue("isSubmitting") ?? false;
  }

  renderElement() {
    if (!this.model.hasActions) return null;

    const primary: Action[] = [];
    const secondary: Action[] = [];
    const hasCompleteAction = this.model.renderedActions.some(
      (action: Action) => action.id === "sv-nav-complete" && action.visible,
    );

    const activePage = this.survey?.activePage;
    const pageNextButtonText = activePage?.getPropertyValue("nextButtonText");
    const centerActions = activePage?.getPropertyValue("centerActions");

    this.model.renderedActions.forEach((action: Action) => {
      if (!action.visible) return;
      if (hasCompleteAction && action.id === "sv-nav-next") return;
      const isPrimary =
        action.id === "sv-nav-next" || action.id === "sv-nav-complete";
      (isPrimary ? primary : secondary).push(action);
    });

    return (
      <div
        style={{
          display: "flex",
          justifyContent: centerActions ? "center" : "space-between",
          width: "100%",
        }}
      >
        {!centerActions && (
          <div style={{ display: "flex", gap: "12px" }}>
            {secondary.map((action) => (
              <ActionButton
                key={action.renderedId}
                action={action}
                isValidatingOnServer={this.isValidatingOnServer}
                isQuestionsLoading={this.isCurrentPageQuestionsLoading}
                isSubmitting={this.isSubmitting}
              />
            ))}
          </div>
        )}
        <div style={{ display: "flex", gap: "12px" }}>
          {primary.map((action) => (
            <ActionButton
              key={action.renderedId}
              action={action}
              isPrimary
              titleOverride={
                action.id === "sv-nav-next" ? pageNextButtonText : undefined
              }
              isValidatingOnServer={this.isValidatingOnServer}
              isQuestionsLoading={this.isCurrentPageQuestionsLoading}
              isSubmitting={this.isSubmitting}
            />
          ))}
        </div>
      </div>
    );
  }
}
